import {takeEvery, takeLatest, put, select, call} from 'redux-saga/effects';
import {toast} from 'react-toastify';
import moment from 'moment';
import {
  getSurveyContextApi,
  getSurveysApi,
  getFeedbackStatsApi,
  getSurveyReplyDiscountApi,
  getSurveySummaryApi,
  sendSurveyReplyApi,
  setSurveyContactStatusApi
} from 'helpers/services/api/feedback';
import {
  createDiscountApiV2,
  updateDiscountApiV2
} from 'helpers/services/api/discountsV2';
import {discountsV2ReplaceZFromDates} from 'helpers/functionUtils/discountsV2';
import {
  GET_FEEDBACK_DATA,
  GET_FEEDBACK_DATA_SUCCESS,
  GET_FEEDBACK_REVIEW_CONTEXT,
  GET_FEEDBACK_REVIEW_CONTEXT_FAILURE,
  GET_FEEDBACK_REVIEW_CONTEXT_SUCCESS,
  GET_FEEDBACK_REVIEWS,
  GET_FEEDBACK_REVIEWS_FAILURE,
  GET_FEEDBACK_REVIEWS_SUCCESS,
  GET_FEEDBACK_SUMMARY,
  GET_FEEDBACK_SUMMARY_FAILURE,
  GET_FEEDBACK_SUMMARY_SUCCESS,
  SEND_FEEDBACK_REPLY,
  SEND_FEEDBACK_REPLY_FAILURE,
  SEND_FEEDBACK_REPLY_SUCCESS,
  SET_FEEDBACK_CONTACT_STATUS,
  SET_FEEDBACK_CONTACT_STATUS_FAILURE,
  SET_FEEDBACK_CONTACT_STATUS_SUCCESS,
  GET_FEEDBACK_DISCOUNT,
  GET_FEEDBACK_DISCOUNT_SUCCESS,
  GET_FEEDBACK_DISCOUNT_FAILURE,
  CREATE_FEEDBACK_DISCOUNT,
  CREATE_FEEDBACK_DISCOUNT_SUCCESS,
  CREATE_FEEDBACK_DISCOUNT_FAILURE,
  UPDATE_FEEDBACK_DISCOUNT,
  UPDATE_FEEDBACK_DISCOUNT_SUCCESS,
  UPDATE_FEEDBACK_DISCOUNT_FAILURE,
  SET_LOADING
} from 'store/feedback/action';
import {
  feedbackType,
  FIRST_SURVEY_PRODUCTION_DATE
} from 'routes/App/feedback/constants';

const selectLocationId = (state) => state.user.locationId;

function* getLocationFeedbackStats() {
  try {
    yield put(SET_LOADING(true));

    const {
      feedbackReducer: {
        filters: {tabIndex, startDate, endDate}
      },
      user: {
        activeLocation: {Timezone, createdAt}
      }
    } = yield select((state) => state);

    const locationDate = () => {
      return moment().utcOffset(Timezone, false).utc();
    };

    const serverStartDate =
      startDate || createdAt || FIRST_SURVEY_PRODUCTION_DATE;
    const serverEndDate = endDate || locationDate().endOf('isoWeek').format();

    const {data} = yield getFeedbackStatsApi(
      feedbackType[tabIndex],
      serverStartDate,
      serverEndDate
    );
    if (data) {
      yield put(GET_FEEDBACK_DATA_SUCCESS(data));
    }
    yield put(SET_LOADING(false));
  } catch (error) {
    yield put(SET_LOADING(false));
    console.log('SAGA ERROR', error);
  }
}

function* getFeedbackSummarySaga() {
  try {
    const locationId = yield select(selectLocationId);
    const {data} = yield call(getSurveySummaryApi, locationId);
    if (data?.data) {
      yield put(GET_FEEDBACK_SUMMARY_SUCCESS(data.data));
    } else {
      yield put(GET_FEEDBACK_SUMMARY_FAILURE());
    }
  } catch (error) {
    yield put(GET_FEEDBACK_SUMMARY_FAILURE());
    toast.error(
      "We couldn't load the reviews summary. Please refresh in a moment."
    );
    console.log('SAGA ERROR', error);
  }
}

function* getFeedbackReviewsSaga({payload = {}}) {
  try {
    const locationId = yield select(selectLocationId);
    const {data} = yield call(getSurveysApi, locationId, {
      page: payload.page ?? 0,
      limit: payload.limit ?? 20,
      status: payload.status ?? null
    });
    if (data?.data) {
      yield put(
        GET_FEEDBACK_REVIEWS_SUCCESS({
          items: data.data.items || [],
          totalCount: data.data.totalCount,
          hasNextPage: data.data.hasNextPage,
          page: data.data.page,
          append: !!payload.append
        })
      );
    } else {
      yield put(GET_FEEDBACK_REVIEWS_FAILURE());
    }
  } catch (error) {
    yield put(GET_FEEDBACK_REVIEWS_FAILURE());
    toast.error("We couldn't load reviews. Please refresh in a moment.");
    console.log('SAGA ERROR', error);
  }
}

function* getFeedbackReviewContextSaga({payload: reviewId}) {
  try {
    const locationId = yield select(selectLocationId);
    const {data} = yield call(getSurveyContextApi, locationId, reviewId);
    if (data?.data) {
      yield put(GET_FEEDBACK_REVIEW_CONTEXT_SUCCESS(data.data));
    } else {
      yield put(GET_FEEDBACK_REVIEW_CONTEXT_FAILURE({reviewId}));
    }
  } catch (error) {
    yield put(GET_FEEDBACK_REVIEW_CONTEXT_FAILURE({reviewId}));
    console.log('SAGA ERROR', error);
  }
}

function* setFeedbackContactStatusSaga({payload}) {
  const {reviewId, status} = payload;
  try {
    const locationId = yield select(selectLocationId);
    const {data} = yield call(
      setSurveyContactStatusApi,
      locationId,
      reviewId,
      status
    );
    const contactedAt = data?.data?.contactedAt || new Date().toISOString();
    yield put(
      SET_FEEDBACK_CONTACT_STATUS_SUCCESS({reviewId, status, contactedAt})
    );
    // Summary counters depend on contact statuses; refresh.
    yield put(GET_FEEDBACK_SUMMARY());
  } catch (error) {
    yield put(SET_FEEDBACK_CONTACT_STATUS_FAILURE({reviewId}));
    toast.error("We couldn't save the action. Please try again.");
    console.log('SAGA ERROR', error);
  }
}

function* sendFeedbackReplySaga({payload}) {
  const {reviewId, subject, body, customerEmail} = payload;
  try {
    const locationId = yield select(selectLocationId);
    const {data} = yield call(sendSurveyReplyApi, locationId, reviewId, {
      subject,
      body
    });
    const sentAt = data?.data?.sentAt || new Date().toISOString();
    yield put(SEND_FEEDBACK_REPLY_SUCCESS({reviewId, sentAt, customerEmail}));
    toast.success(
      'Reply sent to customer. You will be able to see responses in your inbox.',
      {autoClose: 8000}
    );
    yield put(GET_FEEDBACK_SUMMARY());
  } catch (error) {
    yield put(SEND_FEEDBACK_REPLY_FAILURE({reviewId}));
    toast.error("We couldn't send the reply. Please try again.");
    console.log('SAGA ERROR', error);
  }
}

function* getFeedbackDiscountSaga() {
  try {
    const locationId = yield select(selectLocationId);
    if (!locationId) {
      yield put(GET_FEEDBACK_DISCOUNT_SUCCESS(null));
      return;
    }
    const {data} = yield call(getSurveyReplyDiscountApi, locationId);
    yield put(GET_FEEDBACK_DISCOUNT_SUCCESS(data?.data || null));
  } catch (error) {
    yield put(GET_FEEDBACK_DISCOUNT_FAILURE());
    console.log('SAGA ERROR', error);
  }
}

function* createFeedbackDiscountSaga({payload}) {
  const {discount, postCallback, errorCallback} = payload || {};
  try {
    const locationId = yield select(selectLocationId);
    const formatted = discountsV2ReplaceZFromDates(discount);
    const {data, status} = yield call(createDiscountApiV2, {
      ...formatted,
      locationId
    });
    // feedbackDiscount is shared, so a late response would write the previous
    // location's promo into it.
    if ((yield select(selectLocationId)) !== locationId) {
      yield put(CREATE_FEEDBACK_DISCOUNT_FAILURE());
      if (errorCallback) errorCallback();
      return;
    }
    if (status === 200 && data) {
      yield put(CREATE_FEEDBACK_DISCOUNT_SUCCESS(data?.data || data));
      if (postCallback) postCallback();
    } else {
      yield put(CREATE_FEEDBACK_DISCOUNT_FAILURE());
      if (errorCallback) errorCallback();
    }
  } catch (error) {
    yield put(CREATE_FEEDBACK_DISCOUNT_FAILURE());
    if (errorCallback) errorCallback();
    console.log('SAGA ERROR', error);
  }
}

function* updateFeedbackDiscountSaga({payload}) {
  const {id, discount, postCallback, errorCallback} = payload || {};
  try {
    const locationId = yield select(selectLocationId);
    const formatted = discountsV2ReplaceZFromDates(discount);
    const {data, status} = yield call(updateDiscountApiV2, id, formatted);
    if ((yield select(selectLocationId)) !== locationId) {
      yield put(UPDATE_FEEDBACK_DISCOUNT_FAILURE());
      if (errorCallback) errorCallback();
      return;
    }
    if (status === 200) {
      yield put(
        UPDATE_FEEDBACK_DISCOUNT_SUCCESS(
          data?.data || data || {...formatted, id}
        )
      );
      if (postCallback) postCallback();
    } else {
      yield put(UPDATE_FEEDBACK_DISCOUNT_FAILURE());
      if (errorCallback) errorCallback();
    }
  } catch (error) {
    yield put(UPDATE_FEEDBACK_DISCOUNT_FAILURE());
    if (errorCallback) errorCallback();
    console.log('SAGA ERROR', error);
  }
}

function* feedbackSaga() {
  yield takeEvery(GET_FEEDBACK_DATA, getLocationFeedbackStats);
  yield takeEvery(GET_FEEDBACK_SUMMARY, getFeedbackSummarySaga);
  yield takeEvery(GET_FEEDBACK_REVIEWS, getFeedbackReviewsSaga);
  yield takeEvery(GET_FEEDBACK_REVIEW_CONTEXT, getFeedbackReviewContextSaga);
  yield takeEvery(SET_FEEDBACK_CONTACT_STATUS, setFeedbackContactStatusSaga);
  yield takeEvery(SEND_FEEDBACK_REPLY, sendFeedbackReplySaga);
  yield takeLatest(GET_FEEDBACK_DISCOUNT, getFeedbackDiscountSaga);
  yield takeEvery(CREATE_FEEDBACK_DISCOUNT, createFeedbackDiscountSaga);
  yield takeEvery(UPDATE_FEEDBACK_DISCOUNT, updateFeedbackDiscountSaga);
}

export default feedbackSaga;
